(() => {
  const initQuantities = root => {
    root.querySelectorAll('.product-quantity').forEach(control => {
      const input = control.querySelector('input[name="quantity"]');
      const minus = control.querySelector('[data-quantity-step="-1"]');
      const plus = control.querySelector('[data-quantity-step="1"]');
      if (!input || !minus || !plus) return;
      const rules = () => ({min: Math.max(1, Number(input.min) || 1), step: Math.max(1, Number(input.step) || 1), max: input.max ? Number(input.max) : Infinity});
      const normalize = value => {
        const {min, step, max} = rules();
        const number = Number.isFinite(value) ? value : min;
        const upper = Number.isFinite(max) ? min + Math.floor(Math.max(0, max - min) / step) * step : Infinity;
        return Math.min(upper, Math.max(min, min + Math.round((number - min) / step) * step));
      };
      const update = () => {
        const value = normalize(input.valueAsNumber);
        const {min, step, max} = rules();
        minus.disabled = value <= min;
        plus.disabled = value + step > max;
      };
      const commit = value => {
        input.value = String(normalize(value));
        update();
      };
      [minus, plus].forEach(button => button.addEventListener('click', () => {
        commit(normalize(input.valueAsNumber) + Number(button.dataset.quantityStep) * rules().step);
        input.dispatchEvent(new Event('input', {bubbles:true}));
        input.dispatchEvent(new Event('change', {bubbles:true}));
      }));
      input.addEventListener('input', update);
      input.addEventListener('change', () => commit(input.valueAsNumber));
      const variant = input.closest('form')?.querySelector('select[name="id"]');
      variant?.addEventListener('change', () => {
        const option = variant.selectedOptions[0];
        if (!option) return;
        input.min = option.dataset.quantityMin || '1';
        input.step = option.dataset.quantityStep || '1';
        input.max = option.dataset.quantityMax || '';
        commit(input.valueAsNumber);
      });
      commit(input.valueAsNumber);
    });
  };
  const initProduct = (root) => {
    if (!root || root.dataset.productToolsReady === 'true') return;
    root.dataset.productToolsReady = 'true';
    initQuantities(root);

    const main = root.querySelector('[data-product-main-image]') || root.querySelector('.product-gallery__main img');
    const thumbsWrap = root.querySelector('[data-product-thumbs]') || root.querySelector('.product-gallery__thumbs');
    const thumbs = Array.from(root.querySelectorAll('[data-product-thumb], .product-gallery__thumbs button[data-photo]'));
    if (!main || !thumbs.length) return;

    let activeIndex = Math.max(0, thumbs.findIndex((thumb) =>
      thumb.getAttribute('aria-current') === 'true' || thumb.getAttribute('aria-pressed') === 'true'
    ));
    let selectionVersion = 0;
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

    const keepThumbVisible = (thumb) => {
      if (!thumbsWrap || !thumb) return;
      const left = thumbsWrap.scrollLeft + thumb.getBoundingClientRect().left - thumbsWrap.getBoundingClientRect().left - (thumbsWrap.clientWidth - thumb.offsetWidth) / 2;
      thumbsWrap.scrollTo({ left, behavior: reduceMotion.matches ? 'auto' : 'smooth' });
    };

    const selectImage = (index) => {
      activeIndex = (index + thumbs.length) % thumbs.length;
      const thumb = thumbs[activeIndex];
      const nextSrc = thumb.dataset.imageSrc || thumb.dataset.photo;
      const nextAlt = thumb.dataset.imageAlt || thumb.dataset.photoAlt || main.alt || '';
      if (!nextSrc) return;

      const version = ++selectionVersion;
      main.classList.add('is-changing');
      const preload = new Image();
      preload.onload = () => {
        if (version !== selectionVersion) return;
        if (thumb.dataset.imageSrcset) {
          main.sizes = '(min-width: 901px) 45vw, calc(100vw - 32px)';
          main.srcset = thumb.dataset.imageSrcset;
        } else {
          main.removeAttribute('srcset');
          main.removeAttribute('sizes');
        }
        main.closest('[data-product-gallery-main]')?.style.setProperty('--photo-ratio', thumb.dataset.photoRatio || '1');
        main.src = nextSrc;
        main.alt = nextAlt;
        main.classList.remove('is-changing');
      };
      preload.onerror = () => { if (version === selectionVersion) main.classList.remove('is-changing'); };
      if (thumb.dataset.imageSrcset) {
        preload.sizes = '(min-width: 901px) 45vw, calc(100vw - 32px)';
        preload.srcset = thumb.dataset.imageSrcset;
      }
      preload.src = nextSrc;

      thumbs.forEach((item, i) => {
        const active = i === activeIndex;
        item.classList.toggle('is-active', active);
        item.setAttribute('aria-current', active ? 'true' : 'false');
        item.setAttribute('aria-pressed', active ? 'true' : 'false');
      });

      keepThumbVisible(thumb);
    };

    thumbs.forEach((thumb, index) => {
      thumb.addEventListener('click', (event) => {
        event.preventDefault();
        selectImage(index);
      });
      thumb.addEventListener('keydown', (event) => {
        if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') {
          event.preventDefault();
          const delta = event.key === 'ArrowRight' ? 1 : -1;
          const next = (index + delta + thumbs.length) % thumbs.length;
          thumbs[next].focus();
          selectImage(next);
        }
      });
    });

    const mainWrap = root.querySelector('[data-product-gallery-main]');
    if (mainWrap && thumbs.length > 1) {
      let gesture = null;
      main.draggable = false;
      mainWrap.addEventListener('pointerdown', event => {
        if (!event.isPrimary || event.button !== 0) return;
        gesture = {id: event.pointerId, x: event.clientX, y: event.clientY};
        mainWrap.setPointerCapture?.(event.pointerId);
      });
      mainWrap.addEventListener('pointerup', event => {
        if (!gesture || gesture.id !== event.pointerId) return;
        const dx = event.clientX - gesture.x;
        const dy = event.clientY - gesture.y;
        gesture = null;
        if (Math.abs(dx) >= 45 && Math.abs(dx) > Math.abs(dy) * 1.3) selectImage(activeIndex + (dx < 0 ? 1 : -1));
      });
      mainWrap.addEventListener('pointercancel', () => { gesture = null; });
      mainWrap.addEventListener('keydown', event => {
        if (event.target !== mainWrap) return;
        const positions = {ArrowLeft: activeIndex - 1, ArrowRight: activeIndex + 1, Home: 0, End: thumbs.length - 1};
        if (!(event.key in positions)) return;
        event.preventDefault();
        selectImage(positions[event.key]);
      });
    }

    if (thumbsWrap) {
      thumbsWrap.addEventListener('wheel', (event) => {
        if (Math.abs(event.deltaY) > Math.abs(event.deltaX)) {
          event.preventDefault();
          thumbsWrap.scrollLeft += event.deltaY;
        }
      }, { passive: false });
    }

  };

  const init = () => document.querySelectorAll('[data-product-root], [data-product-tools]').forEach(initProduct);
  document.addEventListener('DOMContentLoaded', init);
  document.addEventListener('shopify:section:load', init);
})();
